<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useSharedProjects } from '@/composables/useApi'

const router = useRouter()
const route = useRoute()
const { data: projects, loading, refresh } = useSharedProjects()

const isOpen = ref(false)
const dropdownRef = ref<HTMLElement>()

const currentSlug = computed(() => route.params.project as string | undefined)

const currentProject = computed(() => {
  if (!currentSlug.value || !projects.value) return null
  return projects.value.find(p => p.slug === currentSlug.value) ?? null
})

// The list is a singleton fetched once, so a project created since (or a first
// fetch that failed) leaves the slug in the URL matching nothing — the button
// then read "Select project" while inside that very project. Re-fetch once per
// unresolved slug; `attempted` keeps that from looping when it stays missing.
const attempted = new Set<string>()
watch([currentSlug, projects, loading], () => {
  const slug = currentSlug.value
  if (!slug || loading.value) return
  if (projects.value?.some(p => p.slug === slug)) return
  if (attempted.has(slug)) return
  attempted.add(slug)
  refresh()
}, { immediate: true })

function selectProject(slug: string) {
  isOpen.value = false
  router.push(`/dashboard/${slug}`)
}

function handleClickOutside(e: MouseEvent) {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    isOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>

<template>
  <div ref="dropdownRef" class="relative">
    <button
      class="w-full flex items-center justify-between px-sm py-[10px] rounded-md bg-surface-container text-body-sm font-body text-text-primary
        ghost-border transition-colors duration-short hover:bg-surface-container-high"
      @click="isOpen = !isOpen"
    >
      <span class="truncate">{{ currentProject?.name ?? currentSlug ?? 'Select project' }}</span>
      <svg class="w-4 h-4 flex-shrink-0 text-text-muted transition-transform duration-short" :class="{ 'rotate-180': isOpen }" viewBox="0 0 24 24" fill="currentColor">
        <path d="M7 10l5 5 5-5z" />
      </svg>
    </button>

    <!-- Dropdown -->
    <Transition
      enter-active-class="transition duration-medium ease-out"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition duration-short ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="isOpen"
        class="absolute left-0 right-0 top-full mt-xs glass-panel py-xs z-50 max-h-60 overflow-y-auto"
      >
        <div v-if="loading && !projects?.length" class="px-sm py-[10px]">
          <p class="text-body-sm text-text-muted">Loading projects…</p>
        </div>
        <div v-else-if="!projects?.length" class="px-sm py-[10px]">
          <p class="text-body-sm text-text-muted mb-sm">No projects yet</p>
          <button
            class="w-full flex items-center gap-sm px-sm py-[10px] text-body-sm font-body text-primary
              hover:text-primary-light hover:bg-surface-container-high rounded-md transition-colors duration-short"
            @click="isOpen = false; router.push('/onboarding/new-project')"
          >
            <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            <span>Create Project</span>
          </button>
        </div>
        <button
          v-for="project in projects"
          :key="project.id"
          class="w-full flex items-center gap-sm px-sm py-[10px] text-body-sm font-body text-text-secondary
            hover:text-text-primary hover:bg-surface-container-high rounded-md transition-colors duration-short"
          :class="{ 'text-primary': currentProject?.id === project.id }"
          @click="selectProject(project.slug)"
        >
          <span
            class="w-2 h-2 rounded-full flex-shrink-0"
            :class="project.parentProjectId ? 'bg-secondary' : 'bg-primary'"
          />
          <span class="truncate" :class="{ 'pl-md': project.parentProjectId }">
            {{ project.name }}
          </span>
        </button>

        <!-- New Project -->
        <div class="border-t border-outline/10 mt-xs pt-xs">
          <button
            class="w-full flex items-center gap-sm px-sm py-[10px] text-body-sm font-body text-text-muted
              hover:text-text-primary hover:bg-surface-container-high rounded-md transition-colors duration-short"
            @click="isOpen = false; router.push('/onboarding/new-project')"
          >
            <svg class="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            </svg>
            <span>New Project</span>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>
